import { t } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import React, { startTransition, useEffect, useRef, useState, useCallback } from "react";

import { APP_BASE_NAME, APP_DISPLAY_NAME, APP_LOGO_DATA_URL } from "../../branding";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { addSavedEnvironment } from "../../environments/runtime";
import {
  peekPairingTokenFromUrl,
  stripPairingTokenFromUrl,
  submitServerAuthCredential,
} from "../../environments/primary";
import { readHostedPairingRequest } from "../../hostedPairing";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

export function PairingPendingSurface() {
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-10 text-foreground sm:px-6">
      <div className="pointer-events-none absolute inset-0 opacity-80">
        <div className="absolute inset-x-0 top-0 h-44 bg-[radial-gradient(44rem_16rem_at_top,color-mix(in_srgb,var(--color-emerald-500)_14%,transparent),transparent)]" />
        <div className="absolute inset-y-0 left-0 w-72 bg-[radial-gradient(28rem_18rem_at_left,color-mix(in_srgb,var(--color-sky-500)_10%,transparent),transparent)]" />
        <div className="absolute inset-0 bg-[linear-gradient(145deg,color-mix(in_srgb,var(--background)_90%,var(--color-black))_0%,var(--background)_55%)]" />
      </div>

      <section className="relative w-full max-w-xl rounded-2xl border border-border/80 bg-card/90 p-6 shadow-2xl shadow-black/20 backdrop-blur-md sm:p-8">
        <p className="text-[11px] font-semibold tracking-[0.18em] text-muted-foreground uppercase">
          {APP_DISPLAY_NAME}
        </p>
        <h1 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl">
          <Trans>Pairing with this environment</Trans>
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          <Trans>Validating the pairing link and preparing your session.</Trans>
        </p>
      </section>
    </div>
  );
}

export function PairingRouteSurface({
  initialErrorMessage,
  onAuthenticated,
}: {
  initialErrorMessage?: string;
  onAuthenticated: () => void;
}) {
  const { t } = useLingui();
  const isWide = useMediaQuery("sm");
  const autoPairTokenRef = useRef<string | null>(peekPairingTokenFromUrl());
  const [credential, setCredential] = useState(() => autoPairTokenRef.current ?? "");
  const [errorMessage, setErrorMessage] = useState(initialErrorMessage ?? "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const autoSubmitAttemptedRef = useRef(false);

  const submitCredential = useCallback(
    async (nextCredential: string) => {
      setIsSubmitting(true);
      setErrorMessage("");

      const submitError = await submitServerAuthCredential(nextCredential).then(
        () => null,
        (error) => errorMessageFromUnknown(error),
      );

      setIsSubmitting(false);

      if (submitError) {
        setErrorMessage(submitError);
        return;
      }

      startTransition(() => {
        onAuthenticated();
      });
    },
    [onAuthenticated],
  );

  const handleSubmit = useCallback(
    async (event?: React.SubmitEvent<HTMLFormElement>) => {
      event?.preventDefault();
      await submitCredential(credential);
    },
    [submitCredential, credential],
  );

  useEffect(() => {
    const token = autoPairTokenRef.current;
    if (!token || autoSubmitAttemptedRef.current) {
      return;
    }

    autoSubmitAttemptedRef.current = true;
    stripPairingTokenFromUrl();
    void submitCredential(token);
  }, [submitCredential]);

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-10 text-foreground sm:px-6">
      <div className="pointer-events-none absolute inset-0 opacity-80">
        <div className="absolute inset-x-0 top-0 h-44 bg-[radial-gradient(44rem_16rem_at_top,color-mix(in_srgb,var(--color-emerald-500)_14%,transparent),transparent)]" />
        <div className="absolute inset-y-0 left-0 w-72 bg-[radial-gradient(28rem_18rem_at_left,color-mix(in_srgb,var(--color-sky-500)_10%,transparent),transparent)]" />
        <div className="absolute inset-0 bg-[linear-gradient(145deg,color-mix(in_srgb,var(--background)_90%,var(--color-black))_0%,var(--background)_55%)]" />
      </div>

      <section className="relative w-full max-w-xl rounded-2xl border border-border/80 bg-card/90 p-6 shadow-2xl shadow-black/20 backdrop-blur-md sm:p-8">
        <img
          src={APP_LOGO_DATA_URL ?? "/apple-touch-icon.png"}
          alt={APP_BASE_NAME}
          className="mb-5 h-auto w-18 select-none"
          draggable={false}
        />
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
          <Trans>Pair with this environment</Trans>
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          <Trans>Enter your personal token to get started</Trans>
        </p>

        <form className="mt-6 space-y-3" onSubmit={(event) => void handleSubmit(event)}>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Input
              id="pairing-token"
              aria-label={t`Pairing token`}
              autoCapitalize="none"
              autoComplete="off"
              autoCorrect="off"
              className="min-w-0 sm:flex-1 [&_input]:h-10.5 [&_input]:leading-10.5 sm:[&_input]:h-8.5 sm:[&_input]:leading-8.5"
              disabled={isSubmitting}
              nativeInput
              onChange={(event) => setCredential(event.currentTarget.value)}
              placeholder={isWide ? t`Your token, e.g. 7K9M2P4R8T6W` : t`Your token`}
              spellCheck={false}
              value={credential}
            />
            <Button className="h-11 shrink-0 sm:h-9" disabled={isSubmitting} type="submit">
              {isSubmitting ? <Trans>Pairing...</Trans> : <Trans>Continue</Trans>}
            </Button>
          </div>

          {errorMessage ? (
            <div
              role="alert"
              className="rounded-lg border border-destructive/30 bg-destructive/6 px-3 py-2 text-sm text-destructive"
            >
              {errorMessage}
            </div>
          ) : null}
        </form>
      </section>
    </div>
  );
}

export function HostedPairingRouteSurface() {
  const { t } = useLingui();
  const hostedPairingRequestRef = useRef(readHostedPairingRequest());
  const [status, setStatus] = useState<"pairing" | "paired" | "error">(() =>
    hostedPairingRequestRef.current ? "pairing" : "error",
  );
  const [message, setMessage] = useState(() =>
    hostedPairingRequestRef.current
      ? t`Connecting to this backend.`
      : t`This pairing link is missing its backend host or token.`,
  );
  const [canRetry, setCanRetry] = useState(false);
  const submitAttemptedRef = useRef(false);
  const tokenSubmittedRef = useRef(false);

  const submitHostedPairingRequest = useCallback(async () => {
    const request = hostedPairingRequestRef.current;

    if (!request) {
      setStatus("error");
      setMessage(t`This pairing link is missing its backend host or token.`);
      setCanRetry(false);
      return;
    }

    if (tokenSubmittedRef.current) {
      setStatus("error");
      setMessage(t`This one-time pairing token was already submitted. Request a new pairing link.`);
      setCanRetry(false);
      return;
    }

    setStatus("pairing");
    setMessage(t`Connecting to this backend.`);
    setCanRetry(false);
    tokenSubmittedRef.current = true;

    try {
      const record = await addSavedEnvironment({
        label: request.label,
        host: request.host,
        pairingCode: request.token,
      });
      setStatus("paired");
      setMessage(t`${record.label} is saved in this browser.`);
    } catch (error) {
      tokenSubmittedRef.current = false;
      setStatus("error");
      setCanRetry(true);
      setMessage(
        t`${errorMessageFromUnknown(error)} If the backend accepted this one-time token, request a new pairing link before retrying.`,
      );
    }
  }, [t]);

  useEffect(() => {
    if (submitAttemptedRef.current) {
      return;
    }
    submitAttemptedRef.current = true;

    stripPairingTokenFromUrl();
    void submitHostedPairingRequest();
  }, [submitHostedPairingRequest]);

  const request = hostedPairingRequestRef.current;

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-10 text-foreground sm:px-6">
      <div className="pointer-events-none absolute inset-0 opacity-80">
        <div className="absolute inset-x-0 top-0 h-44 bg-[radial-gradient(44rem_16rem_at_top,color-mix(in_srgb,var(--color-emerald-500)_14%,transparent),transparent)]" />
        <div className="absolute inset-y-0 left-0 w-72 bg-[radial-gradient(28rem_18rem_at_left,color-mix(in_srgb,var(--color-sky-500)_10%,transparent),transparent)]" />
        <div className="absolute inset-0 bg-[linear-gradient(145deg,color-mix(in_srgb,var(--background)_90%,var(--color-black))_0%,var(--background)_55%)]" />
      </div>

      <section className="relative w-full max-w-xl rounded-2xl border border-border/80 bg-card/90 p-6 shadow-2xl shadow-black/20 backdrop-blur-md sm:p-8">
        <p className="text-[11px] font-semibold tracking-[0.18em] text-muted-foreground uppercase">
          {APP_DISPLAY_NAME}
        </p>
        <h1 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl">
          {status === "paired" ? (
            <Trans>Backend paired</Trans>
          ) : status === "error" ? (
            <Trans>Pairing failed</Trans>
          ) : (
            <Trans>Pairing backend</Trans>
          )}
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{message}</p>

        {request ? (
          <div className="mt-5 rounded-lg border border-border/70 bg-background/55 px-3 py-3 text-xs leading-relaxed text-muted-foreground">
            <Trans>
              Host: <span className="font-mono text-foreground/80">{request.host}</span>
            </Trans>
          </div>
        ) : null}

        {status === "error" ? (
          <div className="mt-5 rounded-lg border border-destructive/30 bg-destructive/6 px-3 py-2 text-sm text-destructive">
            <Trans>
              Verify the backend is reachable from this browser, supports CORS for hosted clients,
              and is served over HTTPS when opening this page from HTTPS.
            </Trans>
          </div>
        ) : null}

        <div className="mt-6 flex flex-wrap gap-2">
          {status === "pairing" ? (
            <Button disabled size="sm">
              <Trans>Pairing...</Trans>
            </Button>
          ) : canRetry ? (
            <Button size="sm" onClick={() => void submitHostedPairingRequest()}>
              <Trans>Try again</Trans>
            </Button>
          ) : null}
          {status === "paired" ? (
            <Button size="sm" variant="outline" onClick={() => (window.location.href = "/")}>
              <Trans>Open app</Trans>
            </Button>
          ) : null}
        </div>
      </section>
    </div>
  );
}

function errorMessageFromUnknown(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  if (typeof error === "string" && error.trim().length > 0) {
    return error;
  }

  return t`Authentication failed.`;
}
